import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useActivation } from "./ActivationContext";
import { getCoachSubsteps } from "./activationPath";
import { useIsMobile } from "../hooks/useMediaQuery";
import s from "../training/training.module.css";
import as from "./activation.module.css";

const RING_R = 18;
const RING_C = 2 * Math.PI * RING_R;

export default function ActivationCoach() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    visible,
    tourActive,
    currentStep,
    currentSubsteps,
    currentSubstep,
    currentSubstepId,
    setSubstep,
    steps,
    completed,
    skipped,
    progressCount,
    totalSteps,
    allDone,
    mobileTourReplay,
    quickStartTourDoneIds,
    collapsed,
    setCollapsed,
    skipStep,
    dismiss,
    refresh,
    markParentInviteSeen,
  } = useActivation();
  const isMobile = useIsMobile(768);
  const navOpts = useMemo(() => ({ isMobile, skipNav: false }), [isMobile]);

  const ringOffset = useMemo(() => {
    const ratio = totalSteps ? progressCount / totalSteps : 0;
    return RING_C * (1 - ratio);
  }, [progressCount, totalSteps]);

  useEffect(() => {
    if (
      visible &&
      currentStep?.id === "parents" &&
      location.pathname.includes("/clients/registration")
    ) {
      markParentInviteSeen();
    }
  }, [visible, currentStep?.id, location.pathname, markParentInviteSeen]);

  if (!visible) return null;

  if (isMobile) return null;

  /* Live tour owns guidance — dock stays out of the way. */
  if (tourActive) return null;

  if (collapsed) {
    return (
      <div className={`${s.trnRoot} ${s.fabWrap} ${as.actFab}`}>
        <button
          type="button"
          className={s.fab}
          onClick={() => setCollapsed(false)}
        >
          <span className={s.fabDot} aria-hidden />
          Шаг {Math.min(progressCount + 1, totalSteps)}/{totalSteps}
        </button>
      </div>
    );
  }

  const isDone = mobileTourReplay
    ? quickStartTourDoneIds.includes(currentStep?.id)
    : completed.includes(currentStep?.id);
  const wasSkipped = skipped.includes(currentStep?.id);
  const subIdx = currentSubsteps.findIndex((x) => x.id === currentSubstepId);

  const openStep = (step) => {
    const subs = getCoachSubsteps(step, navOpts);
    if (subs[0]) setSubstep(step.id, subs[0].id);
    setCollapsed(true);
  };

  const goNextIncomplete = () => {
    refresh();
    const doneSet = new Set(
      mobileTourReplay
        ? [...quickStartTourDoneIds, ...skipped]
        : [...completed, ...skipped],
    );
    const next = steps.find(
      (st) => !doneSet.has(st.id) && st.id !== currentStep?.id,
    );
    if (next) {
      const subs = getCoachSubsteps(next, navOpts);
      if (subs[0]) setSubstep(next.id, subs[0].id);
    }
  };

  const doSkip = () => {
    if (!currentStep) return;
    skipStep(currentStep.id);
    const doneSet = new Set([...completed, ...skipped, currentStep.id]);
    const next = steps.find((st) => !doneSet.has(st.id));
    if (next) {
      const subs = getCoachSubsteps(next, navOpts);
      if (subs[0]) setSubstep(next.id, subs[0].id);
    }
  };

  const focusCurrentSub = () => {
    setCollapsed(true);
    const isNav = currentSubstep?.kind === "nav";
    if (!isNav && currentStep?.route) navigate(currentStep.route);
    window.setTimeout(() => {
      const sel = currentSubstep?.target;
      if (sel) {
        (
          document.querySelector(`${sel}[data-act-active="1"]`) ||
          document.querySelector(sel)
        )?.scrollIntoView?.({
          behavior: "smooth",
          block: isNav ? "nearest" : "center",
        });
      }
    }, isNav ? 120 : 350);
  };

  return (
    <aside className={`${s.trnRoot} ${s.dock} ${as.actDock}`} aria-label="Быстрый старт">
      <div className={s.dockHandle} aria-hidden />
      <header className={s.dockHead}>
        <div className={s.dockHeadMain}>
          <div className={s.ring} aria-hidden>
            <svg viewBox="0 0 44 44">
              <circle className={s.ringTrack} cx="22" cy="22" r={RING_R} />
              <circle
                className={s.ringValue}
                cx="22"
                cy="22"
                r={RING_R}
                strokeDasharray={RING_C}
                strokeDashoffset={ringOffset}
              />
            </svg>
            <span className={s.ringLabel}>
              {progressCount}/{totalSteps}
            </span>
          </div>
          <div>
            <p className={s.kicker}>Быстрый старт</p>
            <h2 className={s.dockTitle}>
              {allDone
                ? "Клуб готов"
                : `Шаг ${Math.min(progressCount + 1, totalSteps)} из ${totalSteps}`}
            </h2>
          </div>
        </div>
        <button
          type="button"
          className={s.iconBtn}
          aria-label="Свернуть"
          onClick={() => setCollapsed(true)}
        >
          −
        </button>
      </header>

      <div className={s.dockBody}>
        {allDone ? (
          <div className={s.card}>
            <h3 className={s.cardTitle}>Быстрый старт завершён</h3>
            <p className={s.cardText}>
              Группа, расписание, тариф, ученик с абонементом и отметка в журнале —
              всё на месте.
            </p>
            <button
              type="button"
              className={s.btnPrimary}
              onClick={() => {
                dismiss();
                navigate("/manage/app");
              }}
            >
              На главную
            </button>
          </div>
        ) : (
          <div className={s.card}>
            <h3 className={s.cardTitle}>{currentStep?.title}</h3>
            {currentSubstep ? (
              <>
                <p className={as.subBadge}>
                  Подшаг {subIdx + 1} из {currentSubsteps.length}
                </p>
                <p className={s.cardTitle} style={{ fontSize: 16, marginTop: 4 }}>
                  {currentSubstep.title}
                </p>
                <p className={s.cardText}>{currentSubstep.hint}</p>
              </>
            ) : (
              <p className={s.cardText}>{currentStep?.hint}</p>
            )}

            {isDone || wasSkipped ? (
              <p className={s.doneLine}>✓ Сделано — можно дальше</p>
            ) : null}

            <div className={s.actions}>
              {!isDone &&
              currentSubsteps.length > 0 &&
              subIdx >= currentSubsteps.length - 1 ? (
                <button
                  type="button"
                  className={s.btnPrimary}
                  onClick={() => refresh()}
                >
                  Проверить шаг
                </button>
              ) : null}
              <button
                type="button"
                className={
                  !isDone &&
                  currentSubsteps.length > 0 &&
                  subIdx >= currentSubsteps.length - 1
                    ? s.btnGhost
                    : s.btnPrimary
                }
                onClick={() => {
                  if (isDone || wasSkipped) goNextIncomplete();
                  else focusCurrentSub();
                }}
              >
                {isDone || wasSkipped
                  ? "Дальше"
                  : currentSubstep?.kind === "nav"
                    ? "Показать в меню"
                    : "Показать где"}
              </button>
              {!isDone && currentStep?.optional ? (
                <button type="button" className={s.btnGhost} onClick={doSkip}>
                  Пропустить
                </button>
              ) : !isDone && currentSubsteps.length === 0 ? (
                <button
                  type="button"
                  className={s.btnGhost}
                  onClick={() => refresh()}
                >
                  Проверить
                </button>
              ) : null}
            </div>

            {currentSubsteps.length > 0 ? (
              <ol className={as.subList}>
                {currentSubsteps.map((sub, i) => {
                  const done = i < subIdx;
                  const current = sub.id === currentSubstepId;
                  return (
                    <li key={sub.id}>
                      <button
                        type="button"
                        className={[
                          as.subItem,
                          current ? as.subItemCurrent : "",
                          done ? as.subItemDone : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => {
                          setSubstep(currentStep.id, sub.id);
                          setCollapsed(true);
                          if (sub.kind === "nav") {
                            window.setTimeout(() => {
                              document
                                .querySelector(sub.target)
                                ?.scrollIntoView?.({
                                  behavior: "smooth",
                                  block: "center",
                                });
                            }, 120);
                          } else {
                            focusCurrentSub();
                          }
                        }}
                      >
                        <span className={as.subNum}>
                          {done ? "✓" : i + 1}
                        </span>
                        <span>{sub.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </div>
        )}

        <ol className={s.steps}>
          {steps.map((step) => {
            const done = mobileTourReplay
              ? quickStartTourDoneIds.includes(step.id)
              : completed.includes(step.id);
            const skip = skipped.includes(step.id) && !done;
            const current = step.id === currentStep?.id;
            const locked =
              !mobileTourReplay &&
              !done &&
              !skip &&
              steps.some(
                (prev) =>
                  prev.index < step.index &&
                  !completed.includes(prev.id) &&
                  !skipped.includes(prev.id),
              );
            return (
              <li key={step.id}>
                <button
                  type="button"
                  className={[
                    s.step,
                    current ? s.stepCurrent : "",
                    done ? s.stepDone : "",
                    skip ? s.stepSkip : "",
                    locked ? s.stepSkip : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={locked}
                  onClick={() => {
                    if (locked) return;
                    openStep(step);
                  }}
                >
                  <span className={s.stepNum}>
                    {done ? "✓" : skip ? "–" : step.index}
                  </span>
                  <span className={s.stepLabel}>{step.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <footer className={s.dockFoot}>
        <p className={s.safeNote}>Данные сохраняются в ваш клуб</p>
        <button
          type="button"
          className={s.btnExit}
          onClick={() => {
            dismiss();
            navigate("/manage/app");
          }}
        >
          Скрыть подсказки
        </button>
      </footer>
    </aside>
  );
}
